import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance, { API_BASE_URL } from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import TrDateInput from '../components/TrDateInput';

interface Proje {
    projeID: number;
    projeAdi: string;
    ada: string | null;
    parsel: string | null;
    eklenmeTarihi: string;
    baslamaTarihi: string | null;
    bitisTarihi: string | null;
    projeLogo: string | null;
    takipNo: string;
    adres: string | null;
    sehir: string | null;
    kdv: number;
    projeDurumu: string;
    aktifMi: boolean;
}

interface ProjeForm {
    projeAdi: string;
    ada: string;
    parsel: string;
    baslamaTarihi: string;
    bitisTarihi: string;
    adres: string;
    sehir: string;
    kdv: number;
    projeDurumu: string;
}

// Derive a mock completion % from project status + dates
const getProgress = (proje: Proje): number => {
    if (proje.projeDurumu === 'Tamamlandı') return 100;
    if (proje.projeDurumu === 'Gelecek') return 0;
    if (!proje.baslamaTarihi || !proje.bitisTarihi) return 40;
    const start = new Date(proje.baslamaTarihi).getTime();
    const end   = new Date(proje.bitisTarihi).getTime();
    const now   = Date.now();
    if (now >= end) return 95;
    if (now <= start) return 5;
    return Math.round(((now - start) / (end - start)) * 90) + 5;
};

const durumuMeta: Record<string, { label: string; bg: string; color: string; dot: string; pulse?: boolean }> = {
    'Devam Ediyor': { label: 'Devam Ediyor', bg: '#fef3c7', color: '#b45309', dot: '#f59e0b', pulse: true },
    'Tamamlandı':   { label: 'Tamamlandı',   bg: '#dcfce7', color: '#15803d', dot: '#22c55e' },
    'Gelecek':      { label: 'Gelecek',       bg: '#e0f2fe', color: '#0369a1', dot: '#38bdf8' },
};
const getDurum = (d: string) => durumuMeta[d] ?? { label: d, bg: '#f1f5f9', color: '#475569', dot: '#94a3b8' };

// Initials avatar for projects without logo
const projeColors = [
    { bg: '#dbeafe', color: '#1d4ed8' }, { bg: '#ede9fe', color: '#6d28d9' },
    { bg: '#dcfce7', color: '#15803d' }, { bg: '#fef3c7', color: '#b45309' },
    { bg: '#fee2e2', color: '#b91c1c' }, { bg: '#ffedd5', color: '#c2410c' },
];
const getProjeColor = (name: string) => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return projeColors[Math.abs(h) % projeColors.length];
};

const Projeler: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [projeler, setProjeler] = useState<Proje[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hoveredRow, setHoveredRow] = useState<number | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [formData, setFormData] = useState<ProjeForm>({
        projeAdi: '', ada: '', parsel: '',
        baslamaTarihi: '', bitisTarihi: '',
        adres: '', sehir: '', kdv: 20, projeDurumu: 'Gelecek'
    });

    const fetchProjeler = async () => {
        try {
            const response = await axiosInstance.get('/api/Projeler');
            setProjeler(response.data);
        } catch {
            toast.error(t('msg.projelerYuklemeHata'));
        }
    };

    useEffect(() => { fetchProjeler(); }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            let logoFileName = null;
            if (selectedFile) {
                const fd = new FormData();
                fd.append('file', selectedFile);
                fd.append('aciklama', 'Proje Logo');
                const up = await axiosInstance.post('/api/Dosyalar/upload', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                logoFileName = up.data.dosyaAdi;
            }
            const response = await axiosInstance.post('/api/Projeler', {
                ...formData, projeLogo: logoFileName, aktifMi: true
            });
            toast.success(t('msg.projeEklendi'));
            setShowModal(false);
            fetchProjeler();
            navigate(`/projeler/${response.data.projeID}`);
        } catch {
            toast.error(t('msg.projeEklenirkenHata'));
        } finally {
            setSaving(false);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.type === 'image/jpeg' || file.type === 'image/png') {
            setSelectedFile(file);
        } else {
            toast.error(t('msg.sadecePngJpg'));
            e.target.value = '';
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedFile(null);
        setFormData({ projeAdi: '', ada: '', parsel: '', baslamaTarihi: '', bitisTarihi: '', adres: '', sehir: '', kdv: 20, projeDurumu: 'Gelecek' });
    };

    const aktifProjeler   = projeler.filter(p => p.projeDurumu === 'Devam Ediyor').length;
    const tamamlananlar   = projeler.filter(p => p.projeDurumu === 'Tamamlandı').length;
    const gelecekProjeler = projeler.filter(p => p.projeDurumu === 'Gelecek').length;

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

                .pj-page {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    padding: 24px;
                    background: #ffffff;
                }

                /* ── Header ── */
                .pj-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 40px; flex-wrap: wrap; gap: 14px; }
                .pj-header-left { display: flex; flex-direction: column; gap: 3px; }
                .pj-title  { font-size: 20px; font-weight: 600; color: #111827; letter-spacing: -0.4px; margin: 0; }
                .pj-subtitle { font-size: 13px; color: #94a3b8; }
                .pj-breadcrumb { font-size: 13px; color: #6b7280; margin-bottom: 4px; display: flex; align-items: center; }
                .pj-breadcrumb .sep { margin: 0 6px; }
                .pj-breadcrumb .current { color: #1a1a1a; font-weight: 500; }
                .pj-add-btn {
                    display: inline-flex; align-items: center; gap: 8px;
                    padding: 10px 20px; background: #cd6906; color: #fff;
                    border: none; border-radius: 8px;
                    font-size: 13.5px; font-weight: 600; font-family: inherit;
                    cursor: pointer; white-space: nowrap;
                    box-shadow: 0 4px 14px rgba(205,105,6,0.32);
                    transition: background 0.15s, transform 0.12s, box-shadow 0.15s;
                }
                .pj-add-btn:hover { background: #b85d05; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(205,105,6,0.3); }
                .pj-add-btn svg { width: 15px; height: 15px; stroke: #fff; stroke-width: 2.5; fill: none; }

                /* ── Stat cards ── */
                .pj-stats { display: flex; gap: 16px; margin-bottom: 26px; flex-wrap: wrap; }
                .pj-stat {
                    background: #fff; border-radius: 12px; padding: 16px 20px;
                    display: flex; align-items: center; gap: 14px; flex: 1; min-width: 130px;
                    box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 2px 10px rgba(15,23,42,0.04);
                }
                .pj-stat-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
                .pj-stat-icon svg { width: 18px; height: 18px; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
                .pj-stat-val  { font-size: 24px; font-weight: 800; color: #0f172a; line-height: 1; }
                .pj-stat-lbl  { font-size: 12px; color: #94a3b8; font-weight: 500; margin-top: 3px; }

                /* ── Project list ── */
                .pj-list { display: flex; flex-direction: column; gap: 12px; }

                /* ── Project row card ── */
                .pj-row {
                    background: #fff; border-radius: 12px;
                    box-shadow: 0 1px 3px rgba(15,23,42,0.05);
                    padding: 18px 22px;
                    display: flex; align-items: center; gap: 20px;
                    cursor: pointer;
                    transition: transform 0.18s, box-shadow 0.18s, border-color 0.18s;
                    border: 1.5px solid transparent;
                }
                .pj-row:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 20px rgba(15,23,42,0.08);
                    border-color: #e0eaff;
                }

                /* Logo/avatar */
                .pj-logo-wrap { flex-shrink: 0; }
                .pj-logo-img {
                    width: 56px; height: 56px; border-radius: 14px;
                    object-fit: cover;
                    box-shadow: 0 2px 8px rgba(15,23,42,0.12);
                }
                .pj-logo-avatar {
                    width: 56px; height: 56px; border-radius: 14px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 18px; font-weight: 800; letter-spacing: -0.5px;
                }

                /* Identity */
                .pj-identity { flex: 1; min-width: 0; }
                .pj-proje-adi {
                    font-size: 16px; font-weight: 700; color: #0f172a;
                    letter-spacing: -0.2px;
                    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
                }
                .pj-proje-sub { font-size: 12.5px; color: #94a3b8; margin-top: 3px; font-weight: 400; }
                .pj-takip    { font-size: 11px; color: #cbd5e1; margin-top: 2px; font-family: 'SF Mono','Consolas',monospace; }

                /* Progress */
                .pj-progress-col { width: 140px; flex-shrink: 0; }
                .pj-progress-lbl { font-size: 11px; color: #94a3b8; font-weight: 500; margin-bottom: 5px; display: flex; justify-content: space-between; }
                .pj-progress-lbl span { color: #475569; font-weight: 600; }
                .pj-track { height: 5px; background: #f1f5f9; border-radius: 3px; overflow: hidden; }
                .pj-fill  { height: 100%; border-radius: 9999px; transition: width 0.6s ease; }

                /* Status pill */
                .pj-pill {
                    display: inline-flex; align-items: center; gap: 6px;
                    padding: 5px 12px; border-radius: 20px;
                    font-size: 12px; font-weight: 600; white-space: nowrap;
                    flex-shrink: 0;
                }
                .pj-pill-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
                @keyframes pjPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.5)} }
                .pj-pill-dot-pulse { animation: pjPulse 1.5s ease-in-out infinite; }

                /* Detail button */
                .pj-detail-btn {
                    display: inline-flex; align-items: center; gap: 6px;
                    padding: 8px 16px; border-radius: 9px;
                    border: 1.5px solid #e2e8f0; background: transparent;
                    font-size: 13px; font-weight: 600; color: #475569;
                    font-family: inherit; cursor: pointer; white-space: nowrap;
                    flex-shrink: 0;
                    transition: background 0.15s, border-color 0.15s, color 0.15s, transform 0.12s;
                }
                .pj-row:hover .pj-detail-btn {
                    background: #cd6906; border-color: #cd6906; color: #fff;
                    transform: translateX(2px);
                }
                .pj-detail-btn svg { width: 14px; height: 14px; stroke: currentColor; stroke-width: 2.5; fill: none; transition: transform 0.15s; }
                .pj-row:hover .pj-detail-btn svg { transform: translateX(2px); }

                /* Empty */
                .pj-empty { text-align: center; padding: 64px 20px; color: #94a3b8; background: #fff; border-radius: 16px; box-shadow: 0 1px 4px rgba(15,23,42,0.06); }
                .pj-empty-icon { font-size: 40px; margin-bottom: 12px; opacity: 0.5; }
                .pj-empty-text { font-size: 16px; font-weight: 500; }
                .pj-empty-sub  { font-size: 13px; color: #b0bec5; margin-top: 6px; }

                /* ── Modal ── */
                .pj-overlay {
                    position: fixed; inset: 0;
                    background: rgba(15,23,42,0.45);
                    backdrop-filter: blur(3px);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 1050; padding: 20px;
                }
                .pj-modal {
                    background: #fff; border-radius: 18px;
                    width: 100%; max-width: 680px; max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 20px 60px rgba(15,23,42,0.25);
                    animation: pjModalIn 0.18s ease;
                }
                @keyframes pjModalIn { from{opacity:0;transform:translateY(-14px) scale(0.97)} to{opacity:1;transform:translateY(0) scale(1)} }
                .pj-modal-header {
                    padding: 22px 26px 18px; border-bottom: 1px solid #f1f5f9;
                    display: flex; align-items: center; justify-content: space-between;
                    position: sticky; top: 0; background: #fff; z-index: 1;
                    border-radius: 18px 18px 0 0;
                }
                .pj-modal-title  { font-size: 17px; font-weight: 700; color: #0f172a; margin: 0; }
                .pj-modal-close  {
                    width: 30px; height: 30px; border-radius: 8px; border: none;
                    background: #f1f5f9; color: #64748b; cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 16px; transition: background 0.15s;
                }
                .pj-modal-close:hover { background: #e2e8f0; }
                .pj-modal-body  { padding: 22px 26px; }
                .pj-modal-grid  { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
                .pj-modal-full  { grid-column: 1 / -1; }
                .pj-mfield      { display: flex; flex-direction: column; gap: 6px; }
                .pj-mfield label {
                    font-size: 11.5px; font-weight: 600;
                    text-transform: uppercase; letter-spacing: 0.07em; color: #64748b;
                }
                .pj-mfield input, .pj-mfield select, .pj-mfield textarea {
                    padding: 10px 13px; border: 1.5px solid #e2e8f0; border-radius: 9px;
                    font-size: 14px; font-family: inherit; color: #0f172a;
                    outline: none; background: #fff; box-sizing: border-box; width: 100%;
                    transition: border-color 0.15s, box-shadow 0.15s;
                }
                .pj-mfield input:focus, .pj-mfield select:focus, .pj-mfield textarea:focus {
                    border-color: #111827; box-shadow: 0 0 0 3px rgba(17,24,39,0.08);
                }
                .pj-mfield textarea { resize: vertical; min-height: 76px; line-height: 1.5; }
                .pj-mfield-hint { font-size: 11px; color: #94a3b8; margin-top: 4px; }
                .pj-modal-footer {
                    padding: 16px 26px; border-top: 1px solid #f1f5f9;
                    display: flex; justify-content: flex-end; gap: 10px;
                    position: sticky; bottom: 0; background: #fff;
                    border-radius: 0 0 18px 18px;
                }
                .pj-btn-cancel {
                    padding: 9px 18px; border-radius: 9px; border: 1.5px solid #e2e8f0;
                    background: #fff; color: #64748b;
                    font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer;
                    transition: background 0.15s;
                }
                .pj-btn-cancel:hover { background: #f1f5f9; }
                .pj-btn-save {
                    padding: 10px 20px; border-radius: 8px; border: none;
                    background: #cd6906; color: #fff;
                    font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transition: background 0.15s, transform 0.1s;
                }
                .pj-btn-save:hover:not(:disabled) { background: #b85d05; transform: translateY(-1px); }
                .pj-btn-save:disabled { opacity: 0.65; cursor: not-allowed; }
                .pj-spinner {
                    width: 13px; height: 13px;
                    border: 2px solid rgba(255,255,255,0.4);
                    border-top-color: #fff; border-radius: 50%;
                    animation: pjSpin .7s linear infinite;
                }
                @keyframes pjSpin { to { transform: rotate(360deg); } }

                /* ── Responsive ── */
                @media (max-width: 991px) {
                    .pj-page { padding: 18px 14px; }
                    .pj-row { flex-wrap: wrap; padding: 16px 18px; gap: 12px; }
                    .pj-identity { flex: 1 1 0; min-width: 120px; }
                    .pj-progress-col { width: auto; flex: 1 1 100%; order: 5; }
                    .pj-pill { order: 6; }
                    .pj-detail-btn { order: 7; margin-left: auto; }
                    .pj-stats { gap: 10px; }
                    .pj-stat { min-width: calc(50% - 10px); flex: 0 0 calc(50% - 10px); }
                }

                @media (max-width: 576px) {
                    .pj-page { padding: 14px 8px; }
                    .pj-row { gap: 10px; padding: 14px 14px; }
                    .pj-logo-img, .pj-logo-avatar { width: 44px; height: 44px; border-radius: 11px; font-size: 15px; }
                    .pj-proje-adi { font-size: 14px; }
                    .pj-stat { min-width: 100%; flex: 0 0 100%; }
                    .pj-stat-val { font-size: 20px; }
                    .pj-modal-grid { grid-template-columns: 1fr; }
                    .pj-title { font-size: 18px; }
                    .pj-detail-btn { padding: 7px 12px; font-size: 12px; }
                }
            `}</style>

            <div className="pj-page">
                {/* Header */}
                <div className="pj-header">
                    <div className="pj-header-left">
                        <h1 className="pj-title">{t('proje.yonetimi')}</h1>
                        <p style={{ fontSize: '13.5px', color: '#64748b', margin: '4px 0 0' }}>Aktif projelerinizin ilerleme durumlarını ve satış hedeflerini izleyin.</p>
                    </div>
                    <button className="pj-add-btn" onClick={() => setShowModal(true)}>
                        <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        {t('proje.ekle')}
                    </button>
                </div>

                {/* Stat cards */}
                <div className="pj-stats">
                    <div className="pj-stat">
                        <div className="pj-stat-icon" style={{ background: '#dbeafe' }}>
                            <svg viewBox="0 0 24 24" stroke="#2563eb"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                        </div>
                        <div><div className="pj-stat-val">{projeler.length}</div><div className="pj-stat-lbl">{t('proje.toplam')}</div></div>
                    </div>
                    <div className="pj-stat">
                        <div className="pj-stat-icon" style={{ background: '#fef3c7' }}>
                            <svg viewBox="0 0 24 24" stroke="#b45309"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        </div>
                        <div><div className="pj-stat-val">{aktifProjeler}</div><div className="pj-stat-lbl">{t('proje.devamEdiyor')}</div></div>
                    </div>
                    <div className="pj-stat">
                        <div className="pj-stat-icon" style={{ background: '#dcfce7' }}>
                            <svg viewBox="0 0 24 24" stroke="#15803d"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                        </div>
                        <div><div className="pj-stat-val">{tamamlananlar}</div><div className="pj-stat-lbl">{t('proje.tamamlandi')}</div></div>
                    </div>
                    <div className="pj-stat">
                        <div className="pj-stat-icon" style={{ background: '#e0f2fe' }}>
                            <svg viewBox="0 0 24 24" stroke="#0369a1"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                        </div>
                        <div><div className="pj-stat-val">{gelecekProjeler}</div><div className="pj-stat-lbl">{t('proje.gelecek')}</div></div>
                    </div>
                </div>

                <div className="pj-list">
                    {projeler.length === 0 ? (
                        <div className="pj-empty">
                            <div className="pj-empty-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#cbd5e1" width="48" height="48">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
                                </svg>
                            </div>
                            <div className="pj-empty-text">Henüz proje eklenmedi</div>
                            <div className="pj-empty-sub">Yeni proje eklemek için sağ üstteki butona tıklayın</div>
                        </div>
                    ) : (
                        projeler.map(proje => {
                            const durum    = getDurum(proje.projeDurumu);
                            const progress = getProgress(proje);
                            const avColor  = getProjeColor(proje.projeAdi);
                            const initials = proje.projeAdi.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
                            const subText  = [proje.sehir, proje.adres].filter(Boolean).join(', ') || 'Konum belirtilmemiş';
                            const progressColor = proje.projeDurumu === 'Tamamlandı' ? '#22c55e' : proje.projeDurumu === 'Gelecek' ? '#38bdf8' : '#f59e0b';
                            return (
                                <div key={proje.projeID} className="pj-row"
                                    onClick={() => navigate(`/projeler/${proje.projeID}`)}
                                    onMouseEnter={() => setHoveredRow(proje.projeID)}
                                    onMouseLeave={() => setHoveredRow(null)}
                                >
                                    <div className="pj-logo-wrap">
                                        {proje.projeLogo ? (
                                            <img src={`${API_BASE_URL}/userFiles/${proje.projeLogo}`} alt={proje.projeAdi} className="pj-logo-img" />
                                        ) : (
                                            <div className="pj-logo-avatar" style={{ background: avColor.bg, color: avColor.color }}>{initials}</div>
                                        )}
                                    </div>
                                    <div className="pj-identity">
                                        <div className="pj-proje-adi">{proje.projeAdi}</div>
                                        <div className="pj-proje-sub">{subText}</div>
                                        {proje.takipNo && <div className="pj-takip">{proje.takipNo}</div>}
                                    </div>
                                    <div className="pj-progress-col">
                                        <div className="pj-progress-lbl">İlerleme <span>{progress}%</span></div>
                                        <div className="pj-track">
                                            <div className="pj-fill" style={{ width: `${progress}%`, background: progressColor }} />
                                        </div>
                                    </div>
                                    <span className="pj-pill" style={{ background: durum.bg, color: durum.color }}>
                                        <span className={`pj-pill-dot${durum.pulse ? ' pj-pill-dot-pulse' : ''}`} style={{ background: durum.dot }} />
                                        {durum.label}
                                    </span>
                                    <button className="pj-detail-btn" onClick={e => { e.stopPropagation(); navigate(`/projeler/${proje.projeID}`); }}>
                                        İncele
                                        <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                                    </button>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="pj-overlay" onClick={handleCloseModal}>
                    <div className="pj-modal" onClick={e => e.stopPropagation()}>
                        <div className="pj-modal-header">
                            <h2 className="pj-modal-title">{t('proje.ekle')}</h2>
                            <button className="pj-modal-close" onClick={handleCloseModal} aria-label={t('btn.kapat')}>✕</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="pj-modal-body">
                                <div className="pj-modal-grid">
                                    <div className="pj-mfield pj-modal-full">
                                        <label>{t('proje.projeAdi')} <span style={{ color: '#e11d48' }}>*</span></label>
                                        <input
                                            type="text"
                                            placeholder={t('label.ara')}
                                            value={formData.projeAdi}
                                            onChange={e => setFormData({ ...formData, projeAdi: e.target.value })}
                                            maxLength={50}
                                            required
                                            autoFocus
                                        />
                                    </div>
                                    <div className="pj-mfield">
                                        <label>{t('proje.sehir')}</label>
                                        <input
                                            type="text"
                                            placeholder={t('label.ara')}
                                            value={formData.sehir}
                                            onChange={e => setFormData({ ...formData, sehir: e.target.value })}
                                            maxLength={50}
                                        />
                                    </div>
                                    <div className="pj-mfield">
                                        <label>{t('proje.projeDurumu')}</label>
                                        <select value={formData.projeDurumu} onChange={e => setFormData({ ...formData, projeDurumu: e.target.value })}>
                                            <option value="Gelecek">Gelecek</option>
                                            <option value="Devam Ediyor">Devam Ediyor</option>
                                            <option value="Tamamlandı">{t('durum.tamamlandi')}</option>
                                        </select>
                                    </div>
                                    <div className="pj-mfield">
                                        <label>{t('proje.baslamaTarihi')}</label>
                                        <TrDateInput
                                            value={formData.baslamaTarihi}
                                            onChange={e => setFormData({ ...formData, baslamaTarihi: e.target.value })}
                                        />
                                    </div>
                                    <div className="pj-mfield">
                                        <label>{t('proje.bitisTarihi')}</label>
                                        <TrDateInput
                                            value={formData.bitisTarihi}
                                            onChange={e => setFormData({ ...formData, bitisTarihi: e.target.value })}
                                        />
                                    </div>
                                    <div className="pj-mfield">
                                        <label>{t('label.ada')}</label>
                                        <input type="text" placeholder="Ada no" value={formData.ada} onChange={e => setFormData({ ...formData, ada: e.target.value })} maxLength={50} />
                                    </div>
                                    <div className="pj-mfield">
                                        <label>{t('label.parsel')}</label>
                                        <input type="text" placeholder="Parsel no" value={formData.parsel} onChange={e => setFormData({ ...formData, parsel: e.target.value })} maxLength={50} />
                                    </div>
                                    <div className="pj-mfield">
                                        <label>{t('proje.kdv')}</label>
                                        <input
                                            type="number" min="0" max="100"
                                            value={formData.kdv}
                                            onChange={e => setFormData({ ...formData, kdv: parseInt(e.target.value) || 0 })}
                                        />
                                    </div>
                                    <div className="pj-mfield pj-modal-full">
                                        <label>{t('label.adres')}</label>
                                        <textarea
                                            placeholder="Proje adresi..."
                                            value={formData.adres}
                                            onChange={e => setFormData({ ...formData, adres: e.target.value })}
                                            maxLength={100}
                                        />
                                    </div>
                                    <div className="pj-mfield pj-modal-full">
                                        <label>{t('proje.logo')}</label>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".jpg,.jpeg,.png"
                                            onChange={handleFileChange}
                                        />
                                        <div className="pj-mfield-hint">Sadece JPG veya PNG · Maks. 10 MB</div>
                                    </div>
                                </div>
                            </div>
                            <div className="pj-modal-footer">
                                <button type="button" className="pj-btn-cancel" onClick={handleCloseModal}>{t('btn.iptal')}</button>
                                <button type="submit" className="pj-btn-save" disabled={saving}>
                                    {saving && <span className="pj-spinner" />}
                                    {saving ? 'Kaydediliyor...' : t('btn.kaydet')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default Projeler;